import React, { useState } from 'react';
import { Box } from '@mui/material';
import type { DemoLocale } from './demoData';
import { TeaserImageKey, TeaserObservation, teaserImageAsset, teaserMissingImage } from './buildingTeaserData';
import { getSeverityColor, gallerySeverityColors } from '../../../constants/severityColors';
import { colors, fonts } from './design';
import detailPreview from './buildingTeaserDetailPreview.json';

interface Props {
  image: TeaserImageKey;
  alt: string;
  locale: DemoLocale;
  full?: boolean;
  eager?: boolean;
  finding?: TeaserObservation;
  detailedModel?: boolean;
}

function ImageAsset({ image, alt, locale, full = false, eager = false, finding, detailedModel = false }: Props) {
  const [failed, setFailed] = useState(false);
  const detailed = image === 'building' && detailedModel;
  const asset = detailed ? { src: detailPreview.src, srcSet: detailPreview.srcSet,
    width: detailPreview.width, height: detailPreview.height } : teaserImageAsset(image);
  const box = finding?.media.kind === 'illustrative' ? finding.media.boundingBox
    : finding?.media.kind === 'documented' ? finding.boundingBox : undefined;
  const annotationLabel = finding?.media.kind === 'illustrative'
    ? locale === 'sv' ? 'Illustrationsmarkering' : 'Illustration annotation'
    : locale === 'sv' ? 'Originalmarkering' : 'Original annotation';
  return failed ? <Box role="status" sx={{ p: 3, bgcolor: colors.soft, color: colors.ink }}>
    {locale === 'sv'
      ? 'Bilden kunde inte laddas. Du kan fortfarande välja observationer och läsa underlaget.'
      : 'The image could not be loaded. You can still select observations and read the documentation.'}
  </Box> : <Box sx={{ position: 'relative' }}>
    <Box component="picture" sx={{ display: 'block' }}>
    <source type="image/webp" srcSet={asset.srcSet}
      sizes={full ? '(min-width: 1280px) 1200px, 95vw' : '(min-width: 1320px) 650px, (min-width: 900px) 50vw, calc(100vw - 64px)'} />
    <img src={asset.src} width={asset.width} height={asset.height} alt={alt}
      loading={eager ? 'eager' : 'lazy'} decoding="async" onError={() => setFailed(true)} />
    </Box>
    {finding && image === 'building' && <Box role="img"
      aria-label={`${locale === 'sv' ? 'Markering på 3D-modellen' : 'Marker on the 3D model'} ${finding.id}: ${finding.title}`}
      sx={{
        position: 'absolute', pointerEvents: 'none', boxSizing: 'border-box',
        left: `${detailed ? detailPreview.marker.x : finding.x}%`,
        top: `${detailed ? detailPreview.marker.y : finding.y}%`, transform: 'translate(-50%, -50%)',
        width: 48, height: 32, display: 'grid', placeItems: 'center',
        bgcolor: colors.rustDark, color: colors.white, border: `2px solid ${colors.white}`,
        boxShadow: `0 0 0 2px ${colors.ink}`, borderRadius: 1,
        fontFamily: fonts.mono, fontWeight: 700, fontSize: 11,
      }}>{finding.id}</Box>}
    {finding && image !== 'building' && box && <Box role="img" aria-label={`${annotationLabel} ${finding.id}: ${finding.source.type}`}
      sx={{
        position: 'absolute', pointerEvents: 'none', boxSizing: 'border-box',
        left: `${box.left * 100}%`, top: `${box.top * 100}%`,
        width: `${box.width * 100}%`, height: `${box.height * 100}%`,
        border: `2px solid ${getSeverityColor(finding.source.severity, gallerySeverityColors)}`,
        boxShadow: '0 0 0 1px white',
      }} />}
  </Box>;
}

export function TeaserImage(props: Props) {
  if (props.image !== 'building' && props.finding?.media.kind === 'unavailable') {
    return <Box role="status" sx={{ p: 3, bgcolor: colors.soft, color: colors.ink }}>
      {teaserMissingImage(props.locale, props.finding.year)}
    </Box>;
  }
  const image = props.image !== 'building' && props.finding?.media.kind === 'illustrative'
    ? props.finding.media.image : props.image;
  return <Box sx={{ '& img': { width: '100%', height: 'auto', display: 'block' } }}>
    <ImageAsset key={`${image}-${!!props.detailedModel}`} {...props} image={image} />
  </Box>;
}
